import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DashboardLayoutComponent } from '@syncfusion/ej2-react-layouts';
import Box from '@mui/material/Box';
import AddIcon from '@mui/icons-material/Add';
import ReplayIcon from '@mui/icons-material/Replay';
import Button from '@mui/material/Button';
import PanelWrapper from './PanelWrapper';
import moment from 'moment';
import { useTheme } from '@mui/material/styles';
import {
  ExtendedPanelModel,
  MainDashboardElementDefinition,
  MainDashboardElementType,
} from 'common/models/Dashboard/EditLayout';
import AddDashboardElementDialog from '../Dialogs/Dashboard/AddDashboardElementDialog';
import {
  GetMyUser,
  GetSettingBySettingName,
  UpdateSettingBySettingName,
} from 'services/UsersService';
import { ExtendedUser } from 'common/models/Configuration/User';
import {
  DefaultMainDashboardElements,
  GetDefaultSizeByElementType,
  GetElementTypeById,
  GetMinSizeByElementType,
} from 'util/Definitions/Dashboards/Main';
import UserAvatarComponent from './Graphs/UserAvatarComponent';
import BillingsGaugeGraphComponent from './Graphs/BillingsGaugeGraphComponent';
import OpenJobsTableGraph from './Graphs/OpenJobsTableGraph';
import ActiveInterviewsTableGraph from './Graphs/ActiveInterviewsTableGraph';
import BarGraphStageTotal from './Graphs/BarGraphStageTotal';
import useUnsavedChangesDialog from 'hooks/UseUnsavedChangesDialog';
import ConfirmationDialog from '../Dialogs/Generic/ConfirmationDialog';
import { GetCustomerSettingBySettingName } from 'services/ConfigurationService';
import LinkButtonComponent from './Graphs/LinkButtonComponent';
import ActivitiesGraphv2Component from './Graphs/ActivitiesGraphv2';
import JobPipelineGraphComponent from './Graphs/JobPipelineGraph';
import PlacementFeesGraphComponent from './Graphs/PlacementFeesGraph';
import KpiGaugeGraphComponent from './Graphs/KpiGaugeGraph';
import RatioGraphComponent from './Graphs/RatioGraph';
import CandidatePipelineGraphComponent from './Graphs/CandidatePipelineGraph';
import RevenueGaugeGraphComponent from './Graphs/RevenueGaugeGraphComponent';
import BarGraphRevenueTrend from './Graphs/BarGraphRevenueTrend';
import TopPerformersTableGraph from './Graphs/TopPerformersTableGraph';
import { MenuItem, Paper, TextField, Typography } from '@mui/material';
import { Dashboard } from 'common/models/Dashboard/Dashboard';
import {
  GetDashboardById,
  GetDashboards,
  GetKioskDashboard,
  UpdateElements,
} from 'services/DashboardService';
import { userHasAnyPermission } from 'util/PermissionsUtils';
import { Permission as P } from 'common/models/Permissions';
import ForecastGraph from './Graphs/ForecastGraph';
import OldForeCastGraphComponent from './Graphs/OldForeCastGraphComponent';
import { ActivityMetric } from 'common/models/Reporting/BusinessIntelligence';

interface Props {
  homeDashboard: boolean;
  selectedDashboardId?: number;
  renderDashboardDropDown?: boolean;
  editMode?: boolean;
  kioskMode?: boolean;
  kioskToken?: string | null;
  triggerRefresh?: number | null;
  loadingHandler?: (isLoading: boolean) => void;
  errorHandler?: (message: string) => void;
  kioskErrorHandler?: (message: string) => void;
  setActionHandler?: (action: JSX.Element) => void;
  setDashboardNameHandler?: (dashboardName: string) => void;
  callbackHandler?: (refreshTime: number) => void;
}

const LayoutElementsToString = (elements: ExtendedPanelModel[]) => {
  if (elements.length === 0) return '';
  const s = elements.map((e) => ElementString(e));
  s.sort();
  return s.join('|');
};

const ElementString = (element: ExtendedPanelModel) => {
  const val = `${element.id}|${element.row}|${element.col}|${element.sizeX}|${element.sizeY}`;
  return val;
};

type ElementSettingsMap = Record<string, any>;

const unitWidth: number = 112;
const unitHeight: number = 112;
const gapX: number = 10;
const gapY: number = 10;
const cellSpacing = [gapX, gapY];
const columns = 15;
const mediaQueryMaxWidth = '1000px';

export default function MainDashboardLayout({
  homeDashboard,
  selectedDashboardId,
  renderDashboardDropDown = false,
  editMode = false,
  kioskMode = false,
  kioskToken = null,
  triggerRefresh = null,
  loadingHandler,
  errorHandler,
  setActionHandler,
  setDashboardNameHandler,
  callbackHandler,
  kioskErrorHandler,
}: Props) {
  const [isEditMode, setIsEditMode] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [configHasChanges, setConfigHasChanges] = useState(false);
  const [showResetToDefaultConfirmDialog, setShowResetToDefaultConfirmDialog] = useState(false);
  const [currentUser, setCurrentUser] = useState<ExtendedUser | null>(null);
  const [showAddElementDialog, setShowAddElementDialog] = useState(false);
  const [layoutSwapControl, setLayoutSwapControl] = useState(true);
  const [screenResizedControl, setScreenResizedControl] = useState(false);
  const [initialState, setInitialState] = useState<ExtendedPanelModel[] | null>(null);
  const [lastSavedState, setLastSavedState] = useState<ExtendedPanelModel[] | null>(null);
  const [fetchingSavedState, setFetchingSavedState] = useState(false);
  const [fetchedSavedState, setFetchedSavedState] = useState(false);
  const layoutRef = useRef<DashboardLayoutComponent | null>(null);
  const [changedElementSettings, setChangedElementSettings] = useState<ElementSettingsMap>({});
  const [elements, setElements] = useState<MainDashboardElementDefinition[]>([]);
  const [netRevenueLabel, setNetRevenueLabel] = useState('Net Revenue');
  const [elementsToRemove, setElementsToRemove] = useState<string[]>([]);
  const [dashboards, setDashboards] = useState<Dashboard[]>([]);
  const [selectedDashboard, setSelectedDashboard] = useState<Dashboard | null>(null);
  const [refreshFlag, setRefreshFlag] = useState(false);
  const [refreshInterval, setRefreshInterval] = useState<number | null>(null);
  const theme = useTheme();

  useEffect(() => {
    if (refreshInterval && triggerRefresh) {
      setRefreshFlag((prev) => !prev);
    }
  }, [refreshInterval, triggerRefresh]);

  useEffect(() => {
    const keyNums = Object.keys(changedElementSettings);
    setConfigHasChanges(keyNums.length > 0);
  }, [changedElementSettings]);

  const initialStateString = useMemo(() => {
    if (initialState && initialState.length) return LayoutElementsToString(initialState);
    return '';
  }, [initialState]);

  const layoutChangeHandler = useCallback(() => {
    const api = layoutRef.current;
    if (api) {
      const currentData = api.serialize();
      const currentStringData = LayoutElementsToString(currentData);
      setHasChanges(currentStringData !== initialStateString);

      setElements((prevElements) => {
        const tempElements = [...prevElements];

        currentData.forEach((c) => {
          const elementToUpdate = tempElements.find((e) => e.id === c.id);
          if (elementToUpdate) {
            const indexOfElementToUpdate = tempElements.indexOf(elementToUpdate);
            elementToUpdate.col = c.col ?? elementToUpdate.col;
            elementToUpdate.row = c.row ?? elementToUpdate.row;
            elementToUpdate.sizeX = c.sizeX ?? elementToUpdate.sizeX;
            elementToUpdate.sizeY = c.sizeY ?? elementToUpdate.sizeY;
            tempElements[indexOfElementToUpdate] = elementToUpdate;
          }
        });

        return tempElements;
      });
    }
  }, [initialStateString]);

  const dashboardChangedHandler = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      setElementsToRemove([]);
      const id = +e.target.value;
      const sDashboard = dashboards.find((d) => d.id === id);
      if (sDashboard) {
        setSelectedDashboard(sDashboard);
        if (sDashboard.json.length > 0) {
          renderElements(JSON.parse(sDashboard.json) as ExtendedPanelModel[]);
          await UpdateSettingBySettingName('Default_Dashboard', id.toString(), errorHandler);
        } else {
          renderElements(DefaultMainDashboardElements as ExtendedPanelModel[]);
        }
      }
    },
    [dashboards, kioskToken, errorHandler]
  );

  useEffect(() => {
    const getNetRevenueLabel = async () => {
      const res = await GetCustomerSettingBySettingName('NetRevenueName', kioskToken);
      if (res) setNetRevenueLabel(res);
    };
    getNetRevenueLabel();
  }, [kioskToken]);

  useEffect(() => {
    layoutChangeHandler();
  }, [layoutSwapControl, layoutChangeHandler]);

  useEffect(() => {
    setLayoutSwapControl((prev) => !prev);
  }, [lastSavedState]);

  useEffect(() => {
    const windowResizeHandler = () => setScreenResizedControl((prev) => !prev);
    window.addEventListener('resize', windowResizeHandler);
    return () => window.removeEventListener('resize', windowResizeHandler);
  }, []);

  useEffect(() => {
    loadingHandler && loadingHandler(fetchingSavedState);
  }, [loadingHandler, fetchingSavedState]);

  const renderElements = (panels: ExtendedPanelModel[]) => {
    if (panels) {
      setElements(panels as MainDashboardElementDefinition[]);
      const initialConfigs: ElementSettingsMap = {};
      for (let i = 0; i < panels.length; i++) {
        const e = panels[i];
        const id = e.id ?? '';
        if (e.config) initialConfigs[id] = e.config;
      }
      setLastSavedState(panels);
      setInitialState(panels);
    } else {
      setInitialState(DefaultMainDashboardElements as ExtendedPanelModel[]);
      setElements(DefaultMainDashboardElements);
    }
    setFetchedSavedState(true);
  };

  useEffect(() => {
    const getDashboardData = async () => {
      setFetchingSavedState(true);

      if (kioskMode && kioskToken) {
        const kioskDashboard = await GetKioskDashboard(kioskToken, kioskErrorHandler);
        if (kioskDashboard) {
          setDashboards([kioskDashboard]);
          renderElements(JSON.parse(kioskDashboard.json) as ExtendedPanelModel[]);
          setDashboardNameHandler && setDashboardNameHandler(kioskDashboard.name);

          if (kioskDashboard.refreshTime) {
            setRefreshInterval(kioskDashboard.refreshTime * 60 * 1000);
            callbackHandler && callbackHandler(kioskDashboard.refreshTime);
          } else {
            setRefreshInterval(null);
          }
        }
      } else {
        const userRes = await GetMyUser();
        if (userRes) setCurrentUser(userRes);
        const userId = userRes ? userRes.userID : 0;
        let defaultDashboard;

        if (homeDashboard) {
          let res: Dashboard[] | null = [];
          res = await GetDashboards(errorHandler);
          if (res) {
            res.forEach((dashboard) => {
              if (dashboard.userId !== userId) {
                dashboard.name = `${dashboard.name} (${dashboard.owner})`;
              }
            });
            setDashboards(res);

            const defaultDashboardId = await GetSettingBySettingName(
              'Default_Dashboard',
              errorHandler
            );
            defaultDashboard = defaultDashboardId
              ? res.find((r) => r.id === +defaultDashboardId)
              : res.find((r) => r.isPersonal);
          }
        } else {
          if (selectedDashboardId) {
            const res = await GetDashboardById(selectedDashboardId);
            if (res) {
              setDashboards([res]);
              defaultDashboard = res;
            } else {
              defaultDashboard = null;
            }

            if (
              editMode &&
              (userId === defaultDashboard?.userId ||
                userHasAnyPermission([P.OfficeAdministrator, P.SystemAdministrator]))
            )
              setIsEditMode(true);
          }
        }

        if (defaultDashboard) {
          setDashboardNameHandler && setDashboardNameHandler(defaultDashboard.name);
          setSelectedDashboard(defaultDashboard);
          if (defaultDashboard.json.length > 0) {
            renderElements(JSON.parse(defaultDashboard.json) as ExtendedPanelModel[]);
          } else {
            renderElements(DefaultMainDashboardElements as ExtendedPanelModel[]);
          }
        } else {
          const notFoundDashboard: MainDashboardElementDefinition[] = [
            {
              id: 'Dashboard_NotFound',
              row: 0,
              col: 0,
              sizeX: 15,
              sizeY: 1,
              minSizeX: 15,
              minSizeY: 1,
              maxSizeX: null,
              maxSizeY: null,
              type: 'Dashboard_NotFound',
            },
          ];
          renderElements(notFoundDashboard as ExtendedPanelModel[]);
        }
      }
      setFetchingSavedState(false);
    };
    getDashboardData();
  }, [
    setSelectedDashboard,
    setDashboardNameHandler,
    selectedDashboardId,
    editMode,
    homeDashboard,
    kioskMode,
    kioskToken,
    errorHandler,
  ]);

  const linkButtonConfigChange = useCallback((id: string, newTitle: string, newUrl: string) => {
    setElements((prev) => {
      const tmp = [...prev];
      const objectToUpdate = tmp.find((t) => t.id === id);
      if (objectToUpdate) {
        const indexOfObjectToUpdate = tmp.indexOf(objectToUpdate);
        objectToUpdate.config = { title: newTitle, url: newUrl };
        tmp[indexOfObjectToUpdate] = objectToUpdate;
      }
      return tmp;
    });

    setChangedElementSettings((prev) => {
      const tmp = { ...prev };
      tmp[id] = { title: newTitle, url: newUrl };
      return tmp;
    });
  }, []);

  const activitiesGraphConfigChange = useCallback(
    (
      id: string,
      newTitle: string,
      timeRange: number,
      sourceType: string,
      userFilterIds: number[],
      metrics: string
    ) => {
      setElements((prev) => {
        const tmp = [...prev];
        const objectToUpdate = tmp.find((t) => t.id === id);
        if (objectToUpdate) {
          const indexOfObjectToUpdate = tmp.indexOf(objectToUpdate);
          objectToUpdate.config = {
            title: newTitle,
            timeRange: timeRange,
            userFilterIds: userFilterIds,
            sourceType: sourceType,
            metrics: metrics,
          };
          tmp[indexOfObjectToUpdate] = objectToUpdate;
        }
        return tmp;
      });

      setChangedElementSettings((prev) => {
        const tmp = { ...prev };
        tmp[id] = {
          title: newTitle,
          timeRange: timeRange,
          sourceType: sourceType,
          userFilterIds: userFilterIds,
        };
        return tmp;
      });
    },
    []
  );

  const placementFeesGraphConfigChange = useCallback(
    (id: string, timeRange: number, consultantVal: number) => {
      setElements((prev) => {
        const tmp = [...prev];
        const objectToUpdate = tmp.find((t) => t.id === id);
        if (objectToUpdate) {
          const indexOfObjectToUpdate = tmp.indexOf(objectToUpdate);
          objectToUpdate.config = { timeRange: timeRange, consultant: consultantVal };
          tmp[indexOfObjectToUpdate] = objectToUpdate;
        }
        return tmp;
      });

      setChangedElementSettings((prev) => {
        const tmp = { ...prev };
        tmp[id] = { timeRange: timeRange, consultant: consultantVal };
        return tmp;
      });
    },
    []
  );

  const kpiGraphConfigChange = useCallback(
    (
      id: string,
      title: string,
      timeRange: number,
      type: string,
      sourceType: string,
      sourceVal: number
    ) => {
      setElements((prev) => {
        const tmp = [...prev];
        const objectToUpdate = tmp.find((t) => t.id === id);
        if (objectToUpdate) {
          const indexOfObjectToUpdate = tmp.indexOf(objectToUpdate);
          objectToUpdate.config = {
            title: title,
            timeRange: timeRange,
            kpiType: type,
            sourceType: sourceType,
            sourceId: sourceVal,
          };
          tmp[indexOfObjectToUpdate] = objectToUpdate;
        }
        return tmp;
      });

      setChangedElementSettings((prev) => {
        const tmp = { ...prev };
        tmp[id] = {
          title: title,
          timeRange: timeRange,
          kpiType: type,
          sourceType: sourceType,
          sourceId: sourceVal,
        };
        return tmp;
      });
    },
    []
  );

  const ratioGraphConfigChange = useCallback(
    (
      id: string,
      title: string,
      timeRange: number,
      type: string,
      sourceType: string,
      sourceVal: number
    ) => {
      setElements((prev) => {
        const tmp = [...prev];
        const objectToUpdate = tmp.find((t) => t.id === id);
        if (objectToUpdate) {
          const indexOfObjectToUpdate = tmp.indexOf(objectToUpdate);
          objectToUpdate.config = {
            title: title,
            timeRange: timeRange,
            ratioType: type,
            sourceType: sourceType,
            sourceId: sourceVal,
          };
          tmp[indexOfObjectToUpdate] = objectToUpdate;
        }
        return tmp;
      });

      setChangedElementSettings((prev) => {
        const tmp = { ...prev };
        tmp[id] = {
          title: title,
          timeRange: timeRange,
          ratioType: type,
          sourceType: sourceType,
          sourceId: sourceVal,
        };
        return tmp;
      });
    },
    []
  );

  const topPerformersConfigChange = useCallback(
    (
      id: string,
      title: string,
      userFilterIds: string,
      sourceType: string,
      reportPeriod: number,
      noOfTopConsultants: number
    ) => {
      setElements((prev) => {
        const tmp = [...prev];
        const objectToUpdate = tmp.find((t) => t.id === id);
        if (objectToUpdate) {
          const indexOfObjectToUpdate = tmp.indexOf(objectToUpdate);
          objectToUpdate.config = {
            title,
            userFilterIds,
            sourceType,
            reportPeriod,
            noOfTopConsultants,
          };
          tmp[indexOfObjectToUpdate] = objectToUpdate;
        }
        return tmp;
      });

      setChangedElementSettings((prev) => {
        const tmp = { ...prev };
        tmp[id] = { title, userFilterIds, sourceType, reportPeriod, noOfTopConsultants };
        return tmp;
      });
    },
    []
  );

  const configChangeWithReportPeriod = useCallback(
    (
      id: string,
      title: string,
      userFilterIds: string,
      sourceType: string,
      reportPeriod: number
    ) => {
      setElements((prev) => {
        const tmp = [...prev];
        const objectToUpdate = tmp.find((t) => t.id === id);
        if (objectToUpdate) {
          const indexOfObjectToUpdate = tmp.indexOf(objectToUpdate);
          objectToUpdate.config = { title, userFilterIds, sourceType, reportPeriod };
          tmp[indexOfObjectToUpdate] = objectToUpdate;
        }
        return tmp;
      });

      setChangedElementSettings((prev) => {
        const tmp = { ...prev };
        tmp[id] = { title, userFilterIds, sourceType, reportPeriod };
        return tmp;
      });
    },
    []
  );

  const currentContractorConfigChange = useCallback(
    (id: string, title: string, userFilterIds: string, sourceType: string) => {
      setElements((prev) => {
        const tmp = [...prev];
        const objectToUpdate = tmp.find((t) => t.id === id);
        if (objectToUpdate) {
          const indexOfObjectToUpdate = tmp.indexOf(objectToUpdate);
          objectToUpdate.config = { title, userFilterIds, sourceType };
          tmp[indexOfObjectToUpdate] = objectToUpdate;
        }
        return tmp;
      });

      setChangedElementSettings((prev) => {
        const tmp = { ...prev };
        tmp[id] = { title, userFilterIds, sourceType };
        return tmp;
      });
    },
    []
  );

  const historicalRevenueConfigChange = useCallback(
    (id: string, title: string, userFilterIds: string, sourceType: string) => {
      setElements((prev) => {
        const tmp = [...prev];
        const objectToUpdate = tmp.find((t) => t.id === id);
        if (objectToUpdate) {
          const indexOfObjectToUpdate = tmp.indexOf(objectToUpdate);
          objectToUpdate.config = { title, userFilterIds, sourceType };
          tmp[indexOfObjectToUpdate] = objectToUpdate;
        }
        return tmp;
      });

      setChangedElementSettings((prev) => {
        const tmp = { ...prev };
        tmp[id] = { title, userFilterIds, sourceType };
        return tmp;
      });
    },
    []
  );

  const ForcastConfigChange = useCallback(
    (
      id: string,
      title: string,
      userFilterIds: string,
      sourceType: string,
      reportPeriod: number
    ) => {
      setElements((prev) => {
        const tmp = [...prev];
        const objectToUpdate = tmp.find((t) => t.id === id);
        if (objectToUpdate) {
          const indexOfObjectToUpdate = tmp.indexOf(objectToUpdate);
          objectToUpdate.config = { title, userFilterIds, sourceType, reportPeriod };
          tmp[indexOfObjectToUpdate] = objectToUpdate;
        }
        return tmp;
      });

      setChangedElementSettings((prev) => {
        const tmp = { ...prev };
        tmp[id] = { title, userFilterIds, sourceType, reportPeriod };
        return tmp;
      });
    },
    []
  );

  const jobPipelineConfigChange = useCallback(
    (id: string, title: string, userFilterIds: string, sourceType: string) => {
      setElements((prev) => {
        const tmp = [...prev];
        const objectToUpdate = tmp.find((t) => t.id === id);
        if (objectToUpdate) {
          const indexOfObjectToUpdate = tmp.indexOf(objectToUpdate);
          objectToUpdate.config = { title, userFilterIds, sourceType };
          tmp[indexOfObjectToUpdate] = objectToUpdate;
        }
        return tmp;
      });

      setChangedElementSettings((prev) => {
        const tmp = { ...prev };
        tmp[id] = { title, userFilterIds, sourceType };
        return tmp;
      });
    },
    []
  );

  const openJobsConfigChange = useCallback(
    (id: string, title: string, userFilterIds: string, sourceType: string) => {
      setElements((prev) => {
        const tmp = [...prev];
        const objectToUpdate = tmp.find((t) => t.id === id);
        if (objectToUpdate) {
          const indexOfObjectToUpdate = tmp.indexOf(objectToUpdate);
          objectToUpdate.config = { title, userFilterIds, sourceType };
          tmp[indexOfObjectToUpdate] = objectToUpdate;
        }
        return tmp;
      });

      setChangedElementSettings((prev) => {
        const tmp = { ...prev };
        tmp[id] = { title, userFilterIds, sourceType };
        return tmp;
      });
    },
    []
  );

  const renderElement = useCallback(
    (id: string, type: MainDashboardElementType, config?: any, defaultConfig?: any) => {
      switch (type) {
        case 'UserInfo':
          return <UserAvatarComponent user={currentUser} />;
        case 'Forecast': {
          let forcastTitle = 'Forecast - This Month';
          let forcastType = 'currentuser';
          let forcastFilterIds = '';
          let forcastreportPeriod = 1;
          if (config) {
            forcastTitle = config.title;
            forcastType = config.sourceType;
            forcastFilterIds = config.userFilterIds;
            forcastreportPeriod = config.reportPeriod;
          } else {
            if (defaultConfig) {
              forcastTitle = defaultConfig.title;
              forcastType = defaultConfig.sourceType;
              forcastFilterIds = defaultConfig.userFilterIds;
              forcastreportPeriod = defaultConfig.reportPeriod;
            }
          }
          return (
            <ForecastGraph
              refreshFlag={refreshFlag}
              kioskToken={kioskToken}
              selectedIds={forcastFilterIds}
              reportPeriod={forcastreportPeriod}
              sourceType={forcastType}
              title={forcastTitle}
              showConfigButton={isEditMode}
              saveConfigHandler={ForcastConfigChange}
              id={id}
            />
          );
        }

        case 'ForecastThisMonth':
          return <OldForeCastGraphComponent />;
        case 'ForecastNextMonth':
          return <OldForeCastGraphComponent />;
        case 'BillingsThisMonth':
          return <BillingsGaugeGraphComponent />;
        case 'BillingsThisQtr':
          return <BillingsGaugeGraphComponent />;
        case 'BillingsThisYear':
          return <BillingsGaugeGraphComponent />;
        case 'HistoricalBillings': {
          let revenueTrendTitle = 'Historical Revenue';
          let revenueTrendType = '';
          let revenueTrenduserFilterIds = '';

          if (config) {
            revenueTrendTitle = config.title;
            revenueTrendType = config.sourceType;
            revenueTrenduserFilterIds = config.userFilterIds;
          } else {
            if (defaultConfig) {
              revenueTrendTitle = defaultConfig.title;
              revenueTrendType = defaultConfig.sourceType;
              revenueTrenduserFilterIds = defaultConfig.userFilterIds;
            }
          }
          return (
            <BarGraphRevenueTrend
              refreshFlag={refreshFlag}
              kioskToken={kioskToken}
              title={revenueTrendTitle}
              id={id}
              userFilterIds={revenueTrenduserFilterIds}
              sourceType={revenueTrendType}
              showConfigButton={isEditMode}
              saveConfigHandler={historicalRevenueConfigChange}
            />
          );
        }

        case 'MyOpenJobs': {
          let ojTitle = 'My Open Jobs';
          let ojType = 'currentuser';
          let ojUserFilterIds = '';
          if (config) {
            ojTitle = config.title;
            ojType = config.sourceType;
            ojUserFilterIds = config.userFilterIds;
          } else {
            if (defaultConfig) {
              ojTitle = defaultConfig.title;
              ojType = defaultConfig.sourceType;
              ojUserFilterIds = defaultConfig.userFilterIds;
            }
          }
          return (
            <OpenJobsTableGraph
              refreshFlag={refreshFlag}
              kioskToken={kioskToken}
              gridName="Dashboard/MyOpenJobs"
              id={id}
              sourceType={ojType}
              title={ojTitle}
              userFilterIds={ojUserFilterIds}
              showConfigButton={isEditMode}
              saveConfigHandler={openJobsConfigChange}
            />
          );
        }

        case 'MyActiveInterviews':
          return <ActiveInterviewsTableGraph />;
        case 'CurrentContractors': {
          let stageTitle = 'Current Contractors';
          let stageType = 'currentuser';
          let stageUserFilters = '';
          if (config) {
            stageTitle = config.title;
            stageType = config.sourceType;
            stageUserFilters = config.userFilterIds;
          }
          return (
            <BarGraphStageTotal
              refreshFlag={refreshFlag}
              kioskToken={kioskToken}
              title={stageTitle}
              id={id}
              selectedIds={stageUserFilters}
              sourceType={stageType}
              showConfigButton={isEditMode}
              saveConfigHandler={currentContractorConfigChange}
            />
          );
        }

        case 'LinkButton': {
          const link = config && config.url ? config.url : '';
          const title = config && config.title ? config.title : '';
          return (
            <LinkButtonComponent
              id={id}
              title={title}
              url={link}
              showConfigButton={isEditMode}
              saveConfigHandler={linkButtonConfigChange}
            />
          );
        }

        case 'Activity': {
          const activityTitle = config && config.title ? config.title : 'Activities';
          const timeRange = config && config.timeRange ? config.timeRange : 1;
          let useDefaults = false;
          if (!config) {
            useDefaults = true;
          }
          const defaultMetrics: ActivityMetric[] = [
            {
              id: 1,
              color: '#795548',
              dateBasedOn: 1,
              description: 'Closed BD Calls',
              name: 'BD Calls',
              options: 0,
              status: [2],
              subTypes: [32],
              type: 1,
            },
            {
              id: 2,
              color: '#cddc39',
              dateBasedOn: 1,
              description: '',
              name: 'Client Visit',
              options: 0,
              status: [],
              subTypes: [],
              type: 4,
            },
            {
              id: 3,
              color: '#e91e63',
              dateBasedOn: 0,
              description: '',
              name: 'Floats',
              options: 0,
              status: [2],
              subTypes: [2],
              type: 6,
            },
            {
              id: 4,
              color: '#9c27b0',
              dateBasedOn: 0,
              description: '',
              name: 'Jobs',
              options: 0,
              status: [],
              subTypes: [1, 2, 3, 4, 5],
              type: 3,
            },
            {
              id: 5,
              color: '#ffeb3b',
              dateBasedOn: 0,
              description: 'Exclusive Jobs',
              name: 'Exclusives',
              options: 1,
              status: [],
              subTypes: [1, 2, 3, 4, 5],
              type: 3,
            },
            {
              id: 6,
              color: '#4caf50',
              dateBasedOn: 0,
              description: '',
              name: 'Submissions',
              options: 0,
              status: [],
              subTypes: [1],
              type: 6,
            },
            {
              id: 7,
              color: '#009688',
              dateBasedOn: 0,
              description: '',
              name: 'Interviews',
              options: 0,
              status: [],
              subTypes: [],
              type: 2,
            },
            {
              id: 8,
              color: '#2196f3',
              dateBasedOn: 1,
              description: '',
              name: 'Offers',
              options: 0,
              status: [2],
              subTypes: [14],
              type: 1,
            },
            {
              id: 9,
              color: '#795548',
              dateBasedOn: 0,
              description: 'New Placements (Excludes extensions and convert to perm)',
              name: 'Placements',
              options: 1,
              status: [],
              subTypes: [1, 2, 3, 4, 5],
              type: 5,
            },
          ];
          const metrics =
            config && config.metrics ? config.metrics : JSON.stringify(defaultMetrics);

          let sType = 'currentuser';
          let userFilterIds = [];
          if (config) {
            sType = config.sourceType;
            userFilterIds = config.userFilterIds;
          }
          return (
            <ActivitiesGraphv2Component
              useDefaults={useDefaults}
              refreshFlag={refreshFlag}
              kioskToken={kioskToken}
              id={id}
              title={activityTitle}
              sourceType={sType}
              userFilterIds={userFilterIds}
              timeRange={timeRange}
              showConfigButton={isEditMode}
              metrics={metrics}
              saveConfigHandler={activitiesGraphConfigChange}
            />
          );
        }

        case 'JobPipeline': {
          let jPTitle = 'Job Pipeline';
          let jpType = 'currentuser';
          let jpuserFilterIds = '';
          if (config) {
            jPTitle = config.title;
            jpType = config.sourceType;
            jpuserFilterIds = config.userFilterIds;
          }
          return (
            <JobPipelineGraphComponent
              refreshFlag={refreshFlag}
              kioskToken={kioskToken}
              id={id}
              selectedIds={jpuserFilterIds}
              sourceType={jpType}
              title={jPTitle}
              showConfigButton={isEditMode}
              saveConfigHandler={jobPipelineConfigChange}
            />
          );
        }

        case 'CandidatePipeline':
          return <CandidatePipelineGraphComponent />;
        case 'PlacementFees':
          return <PlacementFeesGraphComponent />;
        case 'KPI': {
          const kpiTitle = config && config.title ? config.title : 'KPI';
          const prefix = config && config.kpiType ? config.kpiType : 'bdCalls';
          const sourceType = config && config.sourceType ? config.sourceType : 'currentuser';
          const sourceId = config && config.sourceId ? config.sourceId : -1;
          const kpiTimeRange = config && config.timeRange ? config.timeRange : 1;
          return (
            <KpiGaugeGraphComponent
              id={id}
              title={kpiTitle}
              sourceType={sourceType}
              sourceId={sourceId}
              timeRange={kpiTimeRange}
              type={prefix}
              showConfigButton={isEditMode}
              saveConfigHandler={kpiGraphConfigChange}
              kioskToken={kioskToken}
              refreshFlag={refreshFlag}
            />
          );
        }

        case 'Ratio': {
          const ratioTitle = config && config.title ? config.title : 'Ratio';
          const ratioType =
            config && config.ratioType ? config.ratioType : 'submissionsToFirstInterviewRatio';
          const ratioSourceType = config && config.sourceType ? config.sourceType : 'currentuser';
          const ratioSourceId = config && config.sourceId ? config.sourceId : -1;
          const ratioTimeRange = config && config.timeRange ? config.timeRange : 1;
          return (
            <RatioGraphComponent
              id={id}
              title={ratioTitle}
              sourceType={ratioSourceType}
              sourceId={ratioSourceId}
              timeRange={ratioTimeRange}
              type={ratioType}
              showConfigButton={isEditMode}
              saveConfigHandler={ratioGraphConfigChange}
              kioskToken={kioskToken}
              refreshFlag={refreshFlag}
            />
          );
        }

        case 'Revenue': {
          let revenueTitle = 'Revenue';
          let sType = 'currentuser';
          let userFilterIds = '';
          let reportPeriod = 1;
          if (config) {
            revenueTitle = config.title;
            sType = config.sourceType;
            userFilterIds = config.userFilterIds;
            reportPeriod = config.reportPeriod;
          } else {
            if (defaultConfig) {
              revenueTitle = defaultConfig.title;
              sType = defaultConfig.sourceType;
              userFilterIds = defaultConfig.userFilterIds;
              reportPeriod = defaultConfig.reportPeriod;
            }
          }

          return (
            <RevenueGaugeGraphComponent
              id={id}
              title={revenueTitle}
              userFilterIds={userFilterIds}
              sourceType={sType}
              reportPeriod={reportPeriod}
              showConfigButton={isEditMode}
              saveConfigHandler={configChangeWithReportPeriod}
              kioskToken={kioskToken}
              refreshFlag={refreshFlag}
            />
          );
        }

        case 'TopPerformers': {
          let tpTitle = 'Top Performers';
          let tpType = 'currentuser';
          let tpUserFilterIds = '';
          let tpReportPeriod = 1;
          let tpTopConsultant = 3;
          if (config) {
            tpTitle = config.title;
            tpType = config.sourceType;
            tpUserFilterIds = config.userFilterIds;
            tpReportPeriod = config.reportPeriod;
            tpTopConsultant = config.noOfTopConsultants;
          }
          return (
            <TopPerformersTableGraph
              id={id}
              title={tpTitle}
              sourceType={tpType}
              userFilterIds={tpUserFilterIds}
              reportPeriod={tpReportPeriod}
              showConfigButton={isEditMode}
              noOfTopConsultants={tpTopConsultant}
              saveConfigHandler={topPerformersConfigChange}
              kioskToken={kioskToken}
              refreshFlag={refreshFlag}
            />
          );
        }

        case 'Dashboard_NotFound':
          return (
            <Paper sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Typography
                variant="subtitle2"
                padding={1}
                textAlign="center"
                fontWeight={600}
                component="div"
              >
                {'Dashboard not found'}
              </Typography>
            </Paper>
          );
        default:
          return <div>{id}</div>;
      }
    },
    [
      currentUser,
      isEditMode,
      kioskToken,
      refreshFlag,
      historicalRevenueConfigChange,
      openJobsConfigChange,
      currentContractorConfigChange,
      linkButtonConfigChange,
      activitiesGraphConfigChange,
      jobPipelineConfigChange,
      netRevenueLabel,
      placementFeesGraphConfigChange,
      kpiGraphConfigChange,
      ratioGraphConfigChange,
      configChangeWithReportPeriod,
      topPerformersConfigChange,
    ]
  );

  const removeElementHandler = useCallback(
    (id: string) => {
      const api = layoutRef.current;
      if (api) {
        setHasChanges(true);
        setConfigHasChanges(true);
        api.removePanel(id);
        setElementsToRemove((prev) => [...prev, id]);
        layoutChangeHandler();
      }
    },
    [layoutChangeHandler]
  );

  const addElementHandler = useCallback(
    (newElementType: MainDashboardElementType) => {
      const api = layoutRef.current;
      if (api) {
        const m = moment().format('YYYYMMDDhhmmssSS');
        const id = `${newElementType}_${m}`;
        const [minX, minY] = GetMinSizeByElementType(newElementType);
        const [defX, defY] = GetDefaultSizeByElementType(newElementType);

        const maxRow = Math.max(...api.panels.map((panel) => panel.row ?? 0));
        api.addPanel({ id: id, col: 0, row: maxRow + 2, sizeX: defX, sizeY: defY });
        api.updatePanel({
          id: id,
          col: 0,
          row: maxRow + 2,
          sizeX: defX,
          sizeY: defY,
          minSizeX: minX,
          minSizeY: minY,
        });

        let defaultConfig: any | null;
        if (newElementType === 'Revenue') {
          const defaultRevenueElement = DefaultMainDashboardElements.find(
            (r) => r.id === 'Revenue_Default'
          );
          if (defaultRevenueElement) {
            defaultConfig = defaultRevenueElement.defaultConfig;
          }
        } else if (newElementType === 'HistoricalBillings') {
          const defaultRevenueElement = DefaultMainDashboardElements.find(
            (r) => r.id === 'HistoricalBillings_Default'
          );
          if (defaultRevenueElement) {
            defaultConfig = defaultRevenueElement.defaultConfig;
          }
        } else if (newElementType === 'MyOpenJobs') {
          const defaultRevenueElement = DefaultMainDashboardElements.find(
            (r) => r.id === 'MyOpenJobs_Default'
          );
          if (defaultRevenueElement) {
            defaultConfig = defaultRevenueElement.defaultConfig;
          }
        }

        const currentData = api.serialize();

        setElements((prevElements) => {
          let newElements = [
            ...prevElements,
            {
              id,
              col: 0,
              row: maxRow + 2,
              sizeX: defX,
              sizeY: defY,
              minSizeX: minX,
              minSizeY: minY,
              defaultConfig,
              type: newElementType,
            } as MainDashboardElementDefinition,
          ];
          elementsToRemove.forEach((e) => {
            newElements = newElements.filter((n) => n.id !== e);
          });

          currentData.forEach((c) => {
            const elementToUpdate = newElements.find((e) => e.id === c.id);
            if (elementToUpdate) {
              const indexOfElementToUpdate = newElements.indexOf(elementToUpdate);
              elementToUpdate.col = c.col ?? elementToUpdate.col;
              elementToUpdate.row = c.row ?? elementToUpdate.row;
              elementToUpdate.sizeX = c.sizeX ?? elementToUpdate.sizeX;
              elementToUpdate.sizeY = c.sizeY ?? elementToUpdate.sizeY;
              newElements[indexOfElementToUpdate] = elementToUpdate;
            }
          });
          setLastSavedState(newElements as ExtendedPanelModel[]);
          return newElements;
        });
        setHasChanges(true);
        setConfigHasChanges(true);
        setLayoutSwapControl((prev) => !prev);
        setShowAddElementDialog(false);
      }
    },
    [elementsToRemove]
  );

  const saveChangesHandler = useCallback(async () => {
    const api = layoutRef.current;
    if (isEditMode) {
      if (api && fetchedSavedState) {
        loadingHandler && loadingHandler(true);
        let tmpElements = [...elements];

        if (elementsToRemove.length > 0)
          elementsToRemove.forEach((e) => {
            tmpElements = tmpElements.filter((t) => t.id !== e);
          });

        if (selectedDashboard) {
          setDashboards((prev) => {
            const temp = [...prev];
            const u = temp.find((t) => t.id === selectedDashboard.id);

            if (u) {
              const indexOfUpdate = temp.indexOf(u);
              u.json = JSON.stringify(tmpElements);
              temp[indexOfUpdate] = u;
            }

            return temp;
          });
          const res = await UpdateElements(
            { id: selectedDashboard.id, json: JSON.stringify(tmpElements) },
            errorHandler
          );
          loadingHandler && loadingHandler(false);
          if (!res) return false;
        }

        setChangedElementSettings({});
        setInitialState(elements as ExtendedPanelModel[]);
        setIsEditMode(false);
        setHasChanges(false);
        return true;
      }
    }
    return true;
  }, [
    isEditMode,
    fetchedSavedState,
    loadingHandler,
    errorHandler,
    changedElementSettings,
    elements,
    elementsToRemove,
    selectedDashboard,
  ]);

  const { unsavedChangesDialog } = useUnsavedChangesDialog(
    hasChanges || configHasChanges,
    saveChangesHandler,
    undefined,
    undefined,
    true
  );

  const resetLayoutHandler = useCallback(() => {
    setElements(DefaultMainDashboardElements);
    setLastSavedState(null);
    setHasChanges(true);
    setShowResetToDefaultConfirmDialog(false);
    setLayoutSwapControl((prev) => !prev);
  }, []);

  const layoutResizeStopHandler = useCallback(() => {
    setScreenResizedControl((prev) => !prev);
    layoutChangeHandler();
  }, [layoutChangeHandler]);

  useEffect(() => {
    const api = layoutRef.current;
    if (api && fetchedSavedState) {
      const refreshTimeout = setTimeout(() => {
        api.refresh();
        api.refresh();
      }, 250);
      return () => clearTimeout(refreshTimeout);
    }
  }, [fetchedSavedState, screenResizedControl]);

  const renderLayout = useCallback(() => {
    if (!fetchedSavedState) return <></>;
    const mediaQuery = mediaQueryMaxWidth ? `max-width: ${mediaQueryMaxWidth}` : undefined;
    const w = window.innerWidth;
    return (
      <DashboardLayoutComponent
        cellSpacing={cellSpacing}
        columns={columns}
        allowDragging={isEditMode}
        allowResizing={isEditMode}
        resizeStop={layoutResizeStopHandler}
        change={layoutChangeHandler}
        ref={(l) => (layoutRef.current = l)}
        mediaQuery={mediaQuery}
        cellAspectRatio={w <= 1000 ? 3 : 1}
      >
        {elements &&
          elements.map((e) => {
            return (
              <PanelWrapper
                key={e.id}
                id={e.id}
                col={e.col}
                row={e.row}
                sizeX={e.sizeX}
                sizeY={e.sizeY}
                minSizeX={e.minSizeX}
                minSizeY={e.minSizeY}
                resizeControl={screenResizedControl}
                resizeIconColor={theme.palette.text.disabled}
                gapX={gapX}
                gapY={gapY}
                unitWidth={unitWidth}
                unitHeight={unitHeight}
                removeHandler={isEditMode ? removeElementHandler : undefined}
              >
                {renderElement(e.id, e.type ?? GetElementTypeById(e.id), e.config, e.defaultConfig)}
              </PanelWrapper>
            );
          })}
      </DashboardLayoutComponent>
    );
  }, [
    fetchedSavedState,
    isEditMode,
    layoutResizeStopHandler,
    layoutChangeHandler,
    elements,
    screenResizedControl,
    theme.palette.text.disabled,
    removeElementHandler,
    renderElement,
    kioskToken,
  ]);

  useEffect(() => {
    if (setActionHandler) {
      const action = (
        <>
          {!isEditMode && (
            <Box display="flex" alignItems="center" gap={2}>
              {renderDashboardDropDown && dashboards.length > 1 && (
                <TextField
                  onChange={dashboardChangedHandler}
                  value={selectedDashboard?.id ?? 0}
                  select
                  fullWidth
                  sx={{ backgroundColor: 'white', width: '250px !important' }}
                >
                  {dashboards.map((d) => (
                    <MenuItem key={d.id} value={d.id}>
                      {d.name}
                    </MenuItem>
                  ))}
                </TextField>
              )}
              {(selectedDashboard?.userId === currentUser?.userID ||
                userHasAnyPermission([P.OfficeAdministrator, P.SystemAdministrator])) && (
                <Button
                  sx={{ width: '133px !important' }}
                  variant="contained"
                  onClick={() => setIsEditMode(true)}
                >
                  Edit Dashboard
                </Button>
              )}
            </Box>
          )}
          {isEditMode && (
            <Button
              color="success"
              variant="contained"
              disabled={!hasChanges && !configHasChanges}
              onClick={saveChangesHandler}
            >
              Save
            </Button>
          )}
        </>
      );
      setActionHandler(action);
    }
  }, [
    isEditMode,
    hasChanges,
    saveChangesHandler,
    setActionHandler,
    configHasChanges,
    dashboards,
    selectedDashboard,
    renderDashboardDropDown,
    currentUser,
    dashboardChangedHandler,
  ]);

  return (
    <>
      {unsavedChangesDialog}
      <ConfirmationDialog
        message="Are you sure you want to reset the current layout to its default form? All changes will be lost"
        title="Confirm Action"
        onClose={() => setShowResetToDefaultConfirmDialog(false)}
        onContinue={resetLayoutHandler}
        open={showResetToDefaultConfirmDialog}
        fullWidth
      />
      <AddDashboardElementDialog
        open={showAddElementDialog}
        closeHandler={() => setShowAddElementDialog(false)}
        addElementHandler={addElementHandler}
      />
      {isEditMode && (
        <div style={{ marginBottom: '10px' }}>
          <span>
            <Button
              variant="contained"
              endIcon={<ReplayIcon />}
              onClick={() => setShowResetToDefaultConfirmDialog(true)}
              sx={{ mr: '5px' }}
            >
              Reset to default
            </Button>
            <Button
              variant="contained"
              endIcon={<AddIcon />}
              onClick={() => setShowAddElementDialog(true)}
            >
              Add Element
            </Button>
          </span>
        </div>
      )}
      <Box bgcolor={(t) => t.palette.background.default} p="10px" height="100%">
        <div className="control-section">
          {layoutSwapControl && renderLayout()}
          {!layoutSwapControl && renderLayout()}
        </div>
      </Box>
    </>
  );
}
